import { CalendarClock } from "lucide-react";
import { NavLink, Outlet } from "react-router-dom";
import { LayoutDashboard, Building2, Search, Upload, Cpu, AudioLines, UserCheck, FileSpreadsheet, HardDrive, Activity, ScrollText, LogOut } from "lucide-react";
import { useAuth } from "../lib/auth";
import { cn } from "../lib/utils";

const NAV = [
  ["/", "Dashboard", LayoutDashboard, "nav-dashboard"],
  ["/clienti", "Clienti e Predictive", Building2, "nav-clienti"],
  ["/ricerca", "Ricerca e Acquisizione", Search, "nav-ricerca"],
  ["/programmate", "Acquisizioni Programmate", CalendarClock, "nav-programmate"],
  ["/caricamento", "Caricamento manuale", Upload, "nav-caricamento"],
  ["/elaborazioni", "Elaborazioni", Cpu, "nav-elaborazioni"],
  ["/registrazioni", "Registrazioni", AudioLines, "nav-registrazioni"],
  ["/interessati", "Clienti Interessati", UserCheck, "nav-interessati"],
  ["/commercial-intelligence", "Commercial Intelligence", UserCheck, "nav-commercial-intelligence"],
  ["/report", "Report", FileSpreadsheet, "nav-report"],
  ["/archivio", "Gestione Archivio", HardDrive, "nav-archivio"],
  ["/diagnostica", "Diagnostica Sistema", Activity, "nav-diagnostica"],
  ["/log", "Log elaborazioni", ScrollText, "nav-log"],
];

export default function Layout() {
  const { user, logout } = useAuth();
  return (
    <div className="flex min-h-screen bg-neutral-50">
      <aside className="w-60 shrink-0 border-r bg-white flex flex-col sticky top-0 h-screen" data-testid="sidebar">
        <div className="px-4 py-5 border-b">
          <div className="font-head text-lg font-bold tracking-tight leading-none">SALES RESCUE<span className="text-[#0047FF]"> AI</span></div>
          <div className="eyebrow mt-1.5">Recupero opportunità</div>
        </div>
        <nav className="flex-1 overflow-y-auto py-2">
          {NAV.map(([to, label, Icon, tid]) => (
            <NavLink key={to} to={to} end={to === "/"} data-testid={tid}
              className={({ isActive }) => cn("nav-link flex items-center gap-2.5 px-4 py-2 text-[13px] border-l-2",
                isActive ? "border-[#0047FF] bg-blue-50/60 text-[#0047FF] font-medium" : "border-transparent text-neutral-700 hover:bg-neutral-100")}>
              <Icon size={16} strokeWidth={1.75} />{label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t px-4 py-3 text-xs">
          <div className="text-neutral-500">Super Amministratore</div>
          <div className="font-medium truncate" data-testid="current-user-email">{user?.email}</div>
          <button onClick={logout} data-testid="logout-btn" className="mt-2 flex items-center gap-1.5 text-neutral-600 hover:text-red-600 transition-colors">
            <LogOut size={14} /> Esci
          </button>
        </div>
      </aside>
      <main className="flex-1 min-w-0">
        <Outlet />
      </main>
    </div>
  );
}

export function Page({ title, subtitle, actions, children, testid }) {
  return (
    <div className="p-6 lg:p-8 fade-in" data-testid={testid}>
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-3xl tracking-tight font-semibold">{title}</h1>
          {subtitle && <p className="text-sm text-neutral-500 mt-1 max-w-3xl">{subtitle}</p>}
        </div>
        <div className="flex gap-2 shrink-0">{actions}</div>
      </div>
      {children}
    </div>
  );
}

export function Panel({ title, actions, children, className, testid }) {
  return (
    <section className={cn("bg-white border rounded-sm", className)} data-testid={testid}>
      {title && (
        <div className="flex items-center justify-between px-4 py-2.5 border-b">
          <h3 className="text-sm font-semibold font-head">{title}</h3>
          <div className="flex gap-2">{actions}</div>
        </div>
      )}
      <div className="p-4">{children}</div>
    </section>
  );
}

export function Field({ label, children, hint }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-neutral-700">{label}</span>
      <div className="mt-1">{children}</div>
      {hint && <span className="text-[11px] text-neutral-500">{hint}</span>}
    </label>
  );
}

export function Kpi({ label, value, testid, accent }) {
  return (
    <div className="bg-white border rounded-sm p-4" data-testid={testid}>
      <div className="eyebrow">{label}</div>
      <div className={cn("font-head text-3xl font-semibold mt-2 tabular-nums", accent && "text-[#0047FF]")}>{value}</div>
    </div>
  );
}
